import { cva } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import { cn } from '../../lib/cn'
import { Avatar, type AvatarTintBySize } from '../../atoms/avatar/avatar'

/**
 * The Profiles (498:8578) and PM Profiles (498:8643) sets: Assigned = Yes / No.
 * The two sets are drawn identically and differ only in their sample people
 * (designers there, project managers here), so they are one component: the role
 * is whoever the caller passes, not a variant.
 *
 * Assigned is a 32px avatar beside the name; unassigned is the word alone on the
 * same 32px row. The text binds the Components board's second "Dark" (#080712),
 * `dark-ink`.
 */
export const profileVariants = cva(
	'inline-flex items-center justify-end gap-2.5 font-atlas-sans text-atlas-body-semibold text-atlas-dark-ink whitespace-nowrap',
	{
		variants: {
			assigned: {
				true: '',
				false: 'h-8',
			},
		},
		defaultVariants: { assigned: false },
	},
)

/** The person a work order is assigned to. */
export interface ProfilePerson {
	name: string
	/** The initials drawn in the avatar, e.g. "ER". */
	initials: string
	/** The avatar's tint, per person; the Figma draws yellow, blue, lila and peach. */
	tint?: AvatarTintBySize['sm']
}

export interface ProfileProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
	/** The assignee; omit it for an unassigned work order. */
	person?: ProfilePerson | null
	/** The text shown when there is no assignee. */
	unassignedLabel?: string
}

/** A work order's assignee (designer or PM) as an avatar and name, or "Unassigned". */
export function Profile({ person, unassignedLabel = 'Unassigned', className, ...props }: ProfileProps) {
	const assigned = Boolean(person)
	return (
		<span {...props} className={cn(profileVariants({ assigned }), className)}>
			{person ? (
				<>
					<Avatar size="sm" initials={person.initials} tint={person.tint} aria-hidden="true" />
					<span className="truncate">{person.name}</span>
				</>
			) : (
				<span className="truncate">{unassignedLabel}</span>
			)}
		</span>
	)
}
